import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { cn } from "@/lib/utils";
import { NAV, ADMIN_NAV, NAV_GROUPS, NAV_BY_PATH } from "@/lib/labels";
import { useAuth } from "@/context/AuthContext";
import { get } from "@/lib/api";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { initials } from "@/lib/format";
import { label } from "@/lib/labels";
import { BrandMark } from "@/components/common/BrandLogo";
import { CommandPalette } from "./CommandPalette";
import { NotificationCenter } from "./NotificationCenter";
import { AssistantProvider, useAssistant } from "@/context/AssistantContext";
import { AssistantDock } from "@/components/assistant/AssistantDock";
import { Conversation } from "@/components/assistant/Conversation";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { useWorkspace, workspace, isWorkspacePath } from "@/lib/workspace";
import { useTopbarSlot } from "@/lib/topbar";

const NavItem = ({ item, onClick, badge, compact }) => {
  const Icon = Icons[item.icon] || Icons.Circle;
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={onClick}
      title={compact ? item.label : undefined}
      aria-label={item.label}
      data-testid={`sidebar-nav-item-${item.to === "/" ? "oversikt" : item.to.replace(/^\//, "").replace(/\//g, "-")}`}
      className={({ isActive }) =>
        cn(
          "group relative flex h-[34px] items-center gap-2.5 rounded-lg text-[13px] text-sidebar-muted transition-colors duration-150 hover:bg-sidebar-muted hover:text-sidebar-foreground focus-visible:outline-none focus-visible:bg-sidebar-muted",
          compact ? "w-9 justify-center px-0" : "px-2.5",
          isActive && "bg-card font-medium text-sidebar-active-foreground shadow-[0_1px_0_rgba(16,24,40,0.04),0_1px_3px_rgba(16,24,40,0.08)] hover:bg-card dark:bg-sidebar-active"
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && !compact && <span className="absolute -left-2 top-2 h-[18px] w-[3px] rounded-full bg-primary" aria-hidden />}
          <Icon className={cn("h-4 w-4 shrink-0", isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground/80")} strokeWidth={1.75} />
          {!compact && <span className="truncate">{item.label}</span>}
          {!compact && badge > 0 && <span className="ml-auto font-mono text-[11px] tabular text-muted-foreground">{badge}</span>}
          {compact && badge > 0 && <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />}
        </>
      )}
    </NavLink>
  );
};

const NavGroup = ({ children }) => <div className="px-2 pb-1 pt-5 text-[11px] font-medium text-muted-foreground first:pt-1">{children}</div>;

const MORE_KEY = "si_nav_more_open";
const readMore = () => {
  try {
    return localStorage.getItem(MORE_KEY) === "1";
  } catch {
    return false;
  }
};

const SidebarContent = ({ onNavigate, compact = false, onToggleCompact }) => {
  const { can, user } = useAuth();
  const location = useLocation();
  const { data: dash } = useQuery({ queryKey: ["dashboard"], queryFn: () => get("/dashboard"), staleTime: 60_000, enabled: can("dashboard:read") });
  const badges = { "/henvendelser": dash?.leads?.new, "/service": (dash?.service?.overdue || 0) + (dash?.issues?.critical || 0), "/planlegging": dash?.conflicts?.count };
  // Project-scoped (external) users never get company-wide surfaces – the server enforces this too; the menu just stays honest.
  const scoped = Array.isArray(user?.scope_project_ids) && user.scope_project_ids.length > 0;
  const hiddenForScoped = ["/kart", "/assistent"];
  const groups = NAV_GROUPS.map((g) => ({ ...g, entries: g.items.map((to) => NAV_BY_PATH[to]).filter((n) => n && can(n.perm) && !(scoped && hiddenForScoped.includes(n.to))) })).filter((g) => g.entries.length);
  const inMore = groups.find((g) => g.collapsible)?.entries.some((n) => location.pathname.startsWith(n.to));
  const [moreOpen, setMoreOpen] = useState(() => readMore() || !!inMore);
  useEffect(() => {
    if (inMore) setMoreOpen(true);
  }, [inMore]);
  const toggleMore = (v) => {
    setMoreOpen(v);
    try {
      localStorage.setItem(MORE_KEY, v ? "1" : "0");
    } catch {
      /* ignore */
    }
  };
  if (compact) {
    return (
      <div className="flex h-full flex-col items-center bg-sidebar text-sidebar-foreground" data-testid="sidebar-compact">
        <div className="flex h-12 items-center justify-center"><BrandMark className="h-5 w-5 text-navy" /></div>
        <nav className="flex flex-1 flex-col items-center gap-px overflow-y-auto pb-3 scrollbar-thin">
          {groups.map((g, gi) => (
            <div key={g.id} className="flex flex-col items-center gap-px">
              {gi > 0 && <div className="my-2 h-px w-5 bg-border" aria-hidden />}
              {g.entries.map((n) => <NavItem key={n.to} item={n} onClick={onNavigate} badge={badges[n.to]} compact />)}
            </div>
          ))}
        </nav>
        <div className="flex flex-col items-center gap-1 pb-2">
          <button type="button" onClick={onToggleCompact} title="Utvid meny" aria-label="Utvid meny" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-sidebar-muted hover:text-foreground" data-testid="sidebar-expand-button"><Icons.PanelLeftOpen className="h-4 w-4" strokeWidth={1.75} /></button>
          <Avatar className="h-6 w-6" title={user?.name}><AvatarFallback className="bg-navy text-[10px] font-medium text-white">{initials(user?.name)}</AvatarFallback></Avatar>
        </div>
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex h-12 items-center gap-2 px-4">
        <BrandMark className="h-5 w-5 text-navy" />
        <span className="truncate text-[13px] font-semibold tracking-tight">Stord Innkvartering</span>
        {onToggleCompact && <button type="button" onClick={onToggleCompact} title="Minimer meny" aria-label="Minimer meny" className="ml-auto -mr-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-sidebar-muted hover:text-foreground" data-testid="sidebar-collapse-button"><Icons.PanelLeftClose className="h-4 w-4" strokeWidth={1.75} /></button>}
      </div>
      <nav className="flex-1 overflow-y-auto px-2 pb-3 pt-1 scrollbar-thin">
        {groups.map((g) =>
          g.collapsible ? (
            <Collapsible key={g.id} open={moreOpen} onOpenChange={toggleMore} data-testid="sidebar-group-more">
              <CollapsibleTrigger asChild>
                <button type="button" className="mt-5 flex h-7 w-full items-center gap-1 rounded-md px-2 text-[11px] font-medium text-muted-foreground transition-colors duration-150 hover:bg-sidebar-muted hover:text-foreground focus-visible:bg-sidebar-muted focus-visible:outline-none" aria-expanded={moreOpen} data-testid="sidebar-more-toggle">
                  <span>{g.label}</span>
                  <span className="text-muted-foreground/60 tabular">· {g.entries.length}</span>
                  <Icons.ChevronDown className={cn("ml-auto h-3.5 w-3.5 transition-transform duration-200", moreOpen && "rotate-180")} strokeWidth={1.75} aria-hidden />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-px pt-1">
                {g.entries.map((n) => (
                  <NavItem key={n.to} item={n} onClick={onNavigate} badge={badges[n.to]} />
                ))}
              </CollapsibleContent>
            </Collapsible>
          ) : (
            <div key={g.id}>
              {g.label ? <NavGroup>{g.label}</NavGroup> : <div className="pt-1" />}
              <div className="space-y-px">
                {g.entries.map((n) => (
                  <NavItem key={n.to} item={n} onClick={onNavigate} badge={badges[n.to]} />
                ))}
              </div>
            </div>
          )
        )}
      </nav>
      <div className="border-t border-sidebar px-2 py-2">
        <a href="/henvendelse" target="_blank" rel="noreferrer" className="flex h-8 items-center gap-2.5 rounded-md px-2 text-[12.5px] text-sidebar-muted transition-colors duration-150 hover:bg-sidebar-muted hover:text-sidebar-foreground" data-testid="sidebar-public-form-link">
          <Icons.Globe className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} /> <span className="truncate">Offentlig henvendelsesskjema</span> <Icons.ArrowUpRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
        </a>
        <div className="flex h-9 items-center gap-2 px-2">
          <Avatar className="h-6 w-6"><AvatarFallback className="bg-navy text-[10px] font-medium text-white">{initials(user?.name)}</AvatarFallback></Avatar>
          <div className="min-w-0 leading-tight">
            <div className="truncate text-[12.5px] font-medium">{user?.name}</div>
            <div className="truncate text-[11px] text-muted-foreground">{label(user?.role)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const IconButton = ({ icon: Icon, label: l, onClick, testId, className, children }) => (
  <button type="button" onClick={onClick} aria-label={l} title={l} className={cn("relative inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:bg-muted", className)} data-testid={testId}>
    <Icon className="h-4 w-4" strokeWidth={1.75} />
    {children}
  </button>
);

export const AppShell = () => (
  <AssistantProvider>
    <AppShellInner />
  </AssistantProvider>
);

const AppShellInner = () => {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const assistant = useAssistant();
  const navigate = useNavigate();
  const location = useLocation();
  const title = [...NAV, ...ADMIN_NAV].find((n) => (n.to === "/" ? location.pathname === "/" : location.pathname.startsWith(n.to)))?.label || "";
  const ws = useWorkspace();
  const slot = useTopbarSlot();
  const inWorkspace = isWorkspacePath(location.pathname);
  // spatial workspaces collapse the sidebar to icons by default; the user's explicit choice wins and is remembered
  const compact = ws.collapsed === null ? inWorkspace : ws.collapsed;
  const hideChrome = inWorkspace && ws.fullscreen;
  useEffect(() => { if (!inWorkspace && ws.fullscreen) workspace.setFullscreen(false); }, [inWorkspace, ws.fullscreen]);
  useEffect(() => { if (ws.mobileNav) setMobileOpen(true); }, [ws.mobileNav]);

  return (
    <div className="flex min-h-screen bg-background">
      {!hideChrome && (
        <aside className={cn("sticky top-0 hidden h-screen shrink-0 flex-col border-r border-sidebar lg:flex", compact ? "w-14" : "w-[224px]")} data-testid="sidebar">
          <SidebarContent compact={compact} onToggleCompact={() => workspace.setCollapsed(!compact)} />
        </aside>
      )}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[272px] border-r-0 bg-sidebar p-0 text-sidebar-foreground">
          <SidebarContent onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className={cn("sticky top-0 z-30 flex h-12 items-center gap-2 border-b bg-background px-3 sm:px-5", inWorkspace && "hidden")} data-testid="topbar">
          <IconButton icon={Icons.Menu} label="Meny" onClick={() => setMobileOpen(true)} testId="mobile-menu-button" className="lg:hidden -ml-1" />
          <div className="flex min-w-0 flex-1 items-center" data-testid="topbar-title-slot">
            {slot ?? <span className="truncate text-[13px] font-medium">{title}</span>}
          </div>
          <div className="flex items-center gap-1">
            <CommandPalette />
            <NotificationCenter />
            <IconButton icon={Icons.Sparkles} label="Assistent (⌘J)" onClick={assistant.toggle} testId="ai-assistant-toggle-button" className={cn(assistant.mode !== "closed" && "bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary")} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-1 inline-flex h-8 items-center gap-1.5 rounded-md pl-1 pr-1.5 transition-colors duration-150 hover:bg-muted focus-visible:outline-none focus-visible:bg-muted" data-testid="user-menu-button" aria-label="Brukermeny">
                  <Avatar className="h-6 w-6"><AvatarFallback className="bg-navy text-[10px] font-medium text-white">{initials(user?.name)}</AvatarFallback></Avatar>
                  <Icons.ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-[13px] font-medium">{user?.name}</div>
                  <div className="text-[12px] font-normal text-muted-foreground">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/felt")}><Icons.Smartphone className="mr-2 h-4 w-4" /> Feltvisning</DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.open("/henvendelse", "_blank")}><Icons.Globe className="mr-2 h-4 w-4" /> Offentlig skjema</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-button"><Icons.LogOut className="mr-2 h-4 w-4" /> Logg ut</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        {assistant.mode === "split" && location.pathname !== "/assistent" ? (
          <ResizablePanelGroup direction="horizontal" className="min-h-0 flex-1" onLayout={(sizes) => sizes?.[1] && assistant.setSplitSize(Math.round(sizes[1]))} data-testid="ai-split-layout">
            <ResizablePanel defaultSize={100 - assistant.splitSize} minSize={35}>
              <main className="scroll-root h-[calc(100vh-3rem)] min-w-0 overflow-y-auto scrollbar-thin">
                <Outlet />
              </main>
            </ResizablePanel>
            <ResizableHandle withHandle className="bg-border/70 dark:bg-white/5" />
            <ResizablePanel defaultSize={assistant.splitSize} minSize={28} maxSize={60}>
              <div className="h-[calc(100vh-3rem)] border-l border-border/70 dark:border-white/5">
                <Conversation variant="split" />
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        ) : (
          <main className="min-w-0 flex-1">
            <Outlet />
          </main>
        )}
      </div>
      <AssistantDock />
    </div>
  );
};
